import React, { useRef, useEffect } from 'react';
import { Plus, X, ChevronUp, ChevronDown } from 'lucide-react';
import { ago, countPlaceholders } from './useOnsitePlan';

export const inputCls = 'w-full bg-secondary/40 border border-border rounded-sm px-2.5 py-1.5 text-xs text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-teal-500/60 transition-colors';

// Highlights [placeholders] still left in a value: amber border until the team fills them in.
const phCls = (v) => (/\[[^\]]*\]/.test(String(v || '')) ? ' !border-amber-500/40 !bg-amber-500/5' : '');

export function AutoText({ value, onChange, rows = 2, placeholder, testId, className = '' }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    el.style.height = 'auto'; el.style.height = `${Math.max(el.scrollHeight, 30)}px`;
  }, [value]);
  return (
    <textarea ref={ref} value={value || ''} rows={rows} placeholder={placeholder} data-testid={testId}
      onChange={(e) => onChange(e.target.value)}
      className={`${inputCls} resize-none leading-relaxed overflow-hidden${phCls(value)} ${className}`} />
  );
}

export function AutoInput({ value, onChange, type = 'text', placeholder, testId, className = '' }) {
  return (
    <input type={type} value={value || ''} placeholder={placeholder} data-testid={testId}
      onChange={(e) => onChange(e.target.value)} className={`${inputCls}${phCls(value)} ${className}`} />
  );
}

export function Field({ label, hint, children, className = '' }) {
  return (
    <label className={`block space-y-1 ${className}`}>
      <span className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-2">{label}{hint && <span className="normal-case tracking-normal text-muted-foreground/60">{hint}</span>}</span>
      {children}
    </label>
  );
}

export function ListEditor({ items = [], onChange, placeholder = 'Punkt…', testId, addLabel = 'Punkt hinzufügen' }) {
  const upd = (i, v) => onChange(items.map((x, j) => (j === i ? v : x)));
  return (
    <div className="space-y-1.5" data-testid={testId}>
      {items.map((it, i) => (
        <div key={i} className="flex items-start gap-1.5 group">
          <span className="text-muted-foreground/40 text-xs mt-1.5 select-none">•</span>
          <AutoText value={it} rows={1} onChange={(v) => upd(i, v)} placeholder={placeholder} testId={testId ? `${testId}-${i}` : undefined} />
          <button onClick={() => onChange(items.filter((_, j) => j !== i))} title="Entfernen" data-testid={testId ? `${testId}-remove-${i}` : undefined}
            className="mt-1.5 text-muted-foreground/30 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-3.5 h-3.5" /></button>
        </div>
      ))}
      <button onClick={() => onChange([...items, ''])} data-testid={testId ? `${testId}-add` : undefined}
        className="inline-flex items-center gap-1 text-[11px] text-teal-400 hover:text-teal-300 transition-colors"><Plus className="w-3 h-3" /> {addLabel}</button>
    </div>
  );
}

export function MoveButtons({ index, total, onMove, onRemove, testId }) {
  return (
    <div className="flex flex-col items-center gap-0.5 shrink-0">
      <button disabled={index === 0} onClick={() => onMove(index, index - 1)} className="text-muted-foreground/40 hover:text-foreground disabled:opacity-20" title="Nach oben"><ChevronUp className="w-3.5 h-3.5" /></button>
      <button disabled={index === total - 1} onClick={() => onMove(index, index + 1)} className="text-muted-foreground/40 hover:text-foreground disabled:opacity-20" title="Nach unten"><ChevronDown className="w-3.5 h-3.5" /></button>
      <button onClick={onRemove} data-testid={testId} className="text-muted-foreground/30 hover:text-red-400 mt-1" title="Entfernen"><X className="w-3.5 h-3.5" /></button>
    </div>
  );
}

export const move = (arr, from, to) => { const a = [...arr]; const [x] = a.splice(from, 1); a.splice(to, 0, x); return a; };

export function Section({ id, icon: Icon, iconClass = 'text-teal-400', title, subtitle, meta, data, right, children, testId }) {
  const ph = countPlaceholders(data);
  return (
    <section id={id} className="scroll-mt-28 bg-card border border-border rounded-sm" data-testid={testId}>
      <header className="flex items-center gap-3 px-4 sm:px-5 py-3 border-b border-border/70 flex-wrap">
        {Icon && <Icon className={`w-4 h-4 shrink-0 ${iconClass}`} strokeWidth={1.7} />}
        <h2 className="font-heading text-base text-foreground">{title}</h2>
        {subtitle && <span className="hidden md:block text-[11px] text-muted-foreground">{subtitle}</span>}
        <span className="ml-auto flex items-center gap-2 flex-wrap justify-end">
          {ph > 0 && <span className="text-[9px] font-mono border border-amber-500/40 bg-amber-500/10 text-amber-300 rounded px-1.5 py-0.5" title="Eckige Klammern sind Platzhalter aus dem Template">{ph} Platzhalter</span>}
          {meta?.by && <span className="text-[10px] text-muted-foreground/70" title={meta.at}>{meta.by} · {ago(meta.at)}</span>}
          {right}
        </span>
      </header>
      <div className="px-4 sm:px-5 py-4 space-y-4">{children}</div>
    </section>
  );
}

export function Segmented({ value, options, onChange, testId }) {
  return (
    <div className="inline-flex border border-border rounded-sm overflow-hidden" data-testid={testId}>
      {options.map((o) => (
        <button key={o.value} onClick={() => onChange(o.value)} data-testid={testId ? `${testId}-${o.value}` : undefined}
          className={`px-2.5 py-1 text-[11px] transition-colors ${value === o.value ? (o.cls || 'bg-teal-600 text-white') : 'text-muted-foreground hover:text-foreground hover:bg-secondary/50'}`}>{o.label}</button>
      ))}
    </div>
  );
}
